/**
 * F-025 PWA cache helpers — allowlist, purge, admin-path guards.
 * Cache names and paths are the single source of truth for Workbox + app code.
 */

export const PWA_API_CACHE = 'fitx-api-allowlist-v1'
export const PWA_SHELL_CACHE = 'fitx-shell-v1'

/** Exact pathnames the service worker may runtime-cache (F-025 §6.9).
 * DASH-SEC-02: `/api/v1/dashboard` may include financial (overdue Invoice amounts) and
 * behavioral (No-Show Risk / Coaching Insight) fields for the logged-in Trainer.
 * Health Profile must never appear in that payload; caches are purged on logout.
 */
export const PWA_CACHEABLE_API_PATHS = Object.freeze([
  '/api/v1/dashboard',
  '/api/v1/client/appointments',
  '/api/v1/client/today',
])

export const PWA_CACHE_MAX_ENTRIES = 14
export const PWA_CACHE_MAX_AGE_SECONDS = 7 * 24 * 60 * 60

const FORBIDDEN_PATH_PREFIXES = Object.freeze([
  '/api/v1/admin',
  '/admin',
])

const FORBIDDEN_EXACT_OR_PREFIX = Object.freeze([
  '/api/v1/clients',
  '/api/v1/client/data_export',
])

/**
 * @param {string} pathname
 * @returns {boolean}
 */
export function isPwaCacheableApiPath(pathname) {
  if (!pathname)
    return false

  const path = pathname.split('?')[0]

  return PWA_CACHEABLE_API_PATHS.includes(path)
}

/**
 * @param {string} pathname
 * @returns {boolean}
 */
export function isPwaForbiddenCachePath(pathname) {
  if (!pathname)
    return true

  const path = pathname.split('?')[0]

  if (FORBIDDEN_PATH_PREFIXES.some(prefix => path === prefix || path.startsWith(`${prefix}/`)))
    return true

  return FORBIDDEN_EXACT_OR_PREFIX.some(prefix => path === prefix || path.startsWith(`${prefix}/`))
}

/**
 * @param {string} [pathname]
 * @returns {boolean}
 */
export function isAdminSpaPath(pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
  return pathname === '/admin' || pathname.startsWith('/admin/')
}

/**
 * Delete all FitX PWA Cache Storage entries (and any residual Workbox caches).
 * Safe to call when Cache Storage API is unavailable.
 * @returns {Promise<void>}
 */
export async function purgePwaCaches() {
  if (typeof caches === 'undefined' || !caches?.keys)
    return

  const keys = await caches.keys()

  await Promise.all(
    keys
      .filter(name => name.startsWith('fitx-') || name.startsWith('workbox-'))
      .map(name => caches.delete(name)),
  )
}

/**
 * Unregister all service workers for this origin.
 * @returns {Promise<void>}
 */
export async function unregisterPwaServiceWorkers() {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker?.getRegistrations)
    return

  const registrations = await navigator.serviceWorker.getRegistrations()

  await Promise.all(registrations.map(registration => registration.unregister()))
}

/**
 * Full logout/deletion cleanup: purge caches and notify any active SW.
 * @returns {Promise<void>}
 */
export async function clearPwaStateOnLogout() {
  await purgePwaCaches()

  if (typeof navigator !== 'undefined' && navigator.serviceWorker?.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'PURGE_CACHES' })
  }
}

/**
 * Whether a Response may be written to the API allowlist cache.
 * Only HTTP 200 — never 401/403 or opaque failures (PWA-SEC-04).
 * @param {Response | undefined | null} response
 * @returns {boolean}
 */
export function isCacheableApiResponse(response) {
  return Boolean(response && response.ok && response.status === 200)
}
